import React, { useState } from "react";
import { useTheme } from "@mui/material";
import "./NewScanV2.scss";
import fileUploadIllustration from "../../../assets/baseVersion/Newscan-fileuplaod.svg";
import NewScanDrawer, { ScanTypeId, NewScanFormData } from "./NewScanDrawer";
import useRolePermissions from "../../../shared/hooks/useRolePermissions";

/* ---------- Device Logos ---------- */
import awsLogo from "../../../assets/baseVersion/aws.svg";
import awsLogoDark from "../../../assets/baseVersion/AWS_DARK.svg";
import aristaLogo from "../../../assets/arista.svg";
import aristaLogoDark from "../../../assets/arista_dark.svg";
import juniperLogo from "../../../assets/juniper.svg";
import juniperLogoDark from "../../../assets/juniper_dark.svg";
import sophosLogo from "../../../assets/sophos_light.svg";
import sophosLogoDark from "../../../assets/sophos_dark.svg";
import debianLogo from "../../../assets/debian.svg";
import debianLogoDark from "../../../assets/debian_dark.svg";
import ubuntuLogo from "../../../assets/ubuntu_white.svg";
import ubuntuLogoDark from "../../../assets/ubuntu_dark.svg";
import ciscoLogo from "../../../assets/cisco.svg";
import ciscoLogoDark from "../../../assets/cisco_dark.svg";
import redhatLogo from "../../../assets/redhat_light.svg";
import redhatLogoDark from "../../../assets/redhat_dark.svg";
import paloaltoLogo from "../../../assets/paloalto.svg";
import paloaltoLogoDark from "../../../assets/paloalto_dark.svg";
import fortinetLogo from "../../../assets/fortinet.svg";
import fortinetLogoDark from "../../../assets/fortinet_scan_dark.svg";
import windowsLogo from "../../../assets/baseVersion/Windows_light.svg";
import windowsLogoDark from "../../../assets/baseVersion/Windows_dark.svg";
import huaweiLogo from "../../../assets/huawei-ar21 1.svg";
import mikrotik from "../../../assets/mikrotik.svg";
import mikrotik_dark from "../../../assets/mikrotik_dark.svg";
import huaweiLogoDark from "../../../assets/huawei-dark.svg";
import edgecoreLogo from "../../../assets/baseVersion/edgecore-logo.svg";
import tplinkLogo from "../../../assets/baseVersion/TPLINK_Logo_2 1.svg";
import tplinkLogoDark from "../../../assets/baseVersion/TPLINK_Logo_dark.svg";
import dellLogo from "../../../assets/baseVersion/dell-seeklogo 2.svg";
import appleLogo from "../../../assets/apple.svg";
import appleLogoDark from "../../../assets/apple_dark.svg";
import syrotechLogo from "../../../assets/syrotech_light.svg";
import syrotechLogoDark from "../../../assets/syrotech_dark.svg";


/* ---------- Icon Components ---------- */


const AwsDiscoveryIcon: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="19" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path
      d="M28 22.5C28 24.43 26.43 26 24.5 26H14C11.79 26 10 24.21 10 22C10 19.95 11.53 18.27 13.56 18.03C14.19 15.19 16.84 13 20 13C22.03 13 23.83 14.01 24.94 15.55C27.07 15.85 28.67 17.68 28.67 19.88"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
    />
    <circle cx="22" cy="20" r="2.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
    <line x1="24" y1="22" x2="26" y2="24" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const AwsDiscoveryPostureIcon: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="19" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path
      d="M28 21.5C28 23.43 26.43 25 24.5 25H14C11.79 25 10 23.21 10 21C10 18.95 11.53 17.27 13.56 17.03C14.19 14.19 16.84 12 20 12C22.03 12 23.83 13.01 24.94 14.55C27.07 14.85 28.67 16.68 28.67 18.88"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
    />
    <path
      d="M21 18L18 19.5V22C18 23.65 19.26 25.19 21 25.7C22.74 25.19 24 23.65 24 22V19.5L21 18Z"
      fill="currentColor"
    />
  </svg>
);



/* On-Prem Discovery – monitor with magnifying glass in a circle */
const OnPremDiscoveryIcon: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="19" stroke="currentColor" strokeWidth="1.5" fill="none" />
    {/* Monitor */}
    <rect x="11" y="12" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <line x1="17" y1="25" x2="17" y2="28" stroke="currentColor" strokeWidth="1.5" />
    <line x1="23" y1="25" x2="23" y2="28" stroke="currentColor" strokeWidth="1.5" />
    <line x1="15" y1="28" x2="25" y2="28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    {/* Magnifying glass */}
    <circle cx="21" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
    <line x1="23" y1="20" x2="25" y2="22" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

/* On-Prem Discovery + Posture – monitor with shield in a circle */
const OnPremDiscoveryPostureIcon: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="19" stroke="currentColor" strokeWidth="1.5" fill="none" />
    {/* Monitor */}
    <rect x="11" y="12" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <line x1="17" y1="25" x2="17" y2="28" stroke="currentColor" strokeWidth="1.5" />
    <line x1="23" y1="25" x2="23" y2="28" stroke="currentColor" strokeWidth="1.5" />
    <line x1="15" y1="28" x2="25" y2="28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    {/* Shield */}
    <path
      d="M20 15L17 16.5V19C17 20.65 18.26 22.19 20 22.7C21.74 22.19 23 20.65 23 19V16.5L20 15Z"
      fill="currentColor"
    />
  </svg>
);

/* On-Prem Posture – shield only in a circle */
const OnPremPostureIcon: React.FC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="20" r="19" stroke="currentColor" strokeWidth="1.5" fill="none" />
    {/* Shield */}
    <path
      d="M20 10L12 14V20C12 25.05 15.36 29.74 20 31C24.64 29.74 28 25.05 28 20V14L20 10Z"
      stroke="currentColor" strokeWidth="1.5" fill="none"
    />
    <path
      d="M20 14L15 16.5V20C15 23.15 17.14 26.06 20 26.8C22.86 26.06 25 23.15 25 20V16.5L20 14Z"
      fill="currentColor"
    />
  </svg>
);

/* ---------- Card Data ---------- */

interface ScanCardData {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}



const awsCards: ScanCardData[] = [
  {
    id: "aws-discovery",
    title: "AWS Discovery",
    description: "Map & Identify cloud infrastructure assets.",
    icon: <AwsDiscoveryIcon />,
  },
  {
    id: "aws-discovery-posture",
    title: "AWS Discovery + Posture",
    description: "Scan and analyse AWS resource configurations",
    icon: <AwsDiscoveryPostureIcon />,
  },
];


const onPremCards: ScanCardData[] = [
  {
    id: "onprem-discovery",
    title: "On-Prem Discovery",
    description: "Map & Identify on-prem infrastructure assets.",
    icon: <OnPremDiscoveryIcon />,
  },
  {
    id: "onprem-discovery-posture",
    title: "On-Prem Discovery + Posture",
    description: "Internal asset mapping with compliance validation.",
    icon: <OnPremDiscoveryPostureIcon />,
  },
  {
    id: "onprem-posture",
    title: "On-Prem Posture",
    description: "In-depth security analysis for existing on-prem assets.",
    icon: <OnPremPostureIcon />,
  },
];

/* ---------- Component ---------- */

const NewScanV2: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const modeClass = isDarkMode ? "dark-mode" : "light-mode";

  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const { hasPermissions } = useRolePermissions();

  const filteredAwsCards = awsCards.filter(card => {
    if (card.id === "aws-discovery-posture") return hasPermissions("aws_discovery_posture");
    return true;
  });

  const filteredOnPremCards = onPremCards.filter(card => {
    if (card.id === "onprem-discovery") return hasPermissions("on_prem_discovery");
    if (card.id === "onprem-posture") return hasPermissions("on_prem_posture");
    if (card.id === "onprem-discovery-posture") return hasPermissions("on_prem_discovery") && hasPermissions("on_prem_posture");
    return true;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerScanType, setDrawerScanType] = useState<ScanTypeId>("onprem-discovery");

  const handleCardClick = (id: string) => {
    setSelectedCard(id);
    setDrawerScanType(id as ScanTypeId);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const renderCard = (card: ScanCardData) => {
    const isClickable = card.id !== "aws-discovery";

    return (
      <div
        key={card.id}
        className={`scan-v2-card ${modeClass}${selectedCard === card.id ? " selected" : ""}${!isClickable ? " non-clickable" : ""}`}
        id={`scan-card-${card.id}`}
        onClick={isClickable ? () => handleCardClick(card.id) : undefined}
        role={isClickable ? "button" : undefined}
        tabIndex={isClickable ? 0 : -1}
        onKeyDown={isClickable ? (e) => e.key === "Enter" && handleCardClick(card.id) : undefined}
      >
        <div className="scan-v2-card-icon">{card.icon}</div>
        <div className={`scan-v2-card-title ${modeClass}`}>{card.title}</div>
        <div className={`scan-v2-card-desc ${modeClass}`}>{card.description}</div>
      </div>
    );
  };

  return (
    <div className={`new-scan-v2 ${modeClass}`}>
      <div className="new-scan-v2-container">
        {/* Left Section (60%) */}
        <div className="new-scan-v2-main">
          <div className={`scan-v2-header ${modeClass}`}>
            <h2>Start your Scan</h2>
            <p>
              Fast, Secure and User-friendly way to scan and Analyse your network security.
            </p>
          </div>

          <div className="scan-v2-content">

            <div className="scan-v2-section">
              <div className="scan-v2-cards-grid grid-2">
                {filteredAwsCards.map(renderCard)}
              </div>
            </div>


            <div className="scan-v2-section">
              <div className="scan-v2-cards-grid grid-3">
                {filteredOnPremCards.map(renderCard)}
              </div>
            </div>
            {/* <div className="scan-v2-illustration left-illustration">
              <img src={fileUploadIllustration} alt="File upload illustration" />
            </div> */}
          </div>
        </div>

        {/* Right Section (40%) */}
        <div className="new-scan-v2-sidebar">
          <div className={`sidebar-placeholder-card ${modeClass}`}>
            <div className="sidebar-header">
              <h4 className="scan-v2-section-title">Connectors</h4>
            </div>

            <div className="supported-devices-grid sidebar-logos">
              {[
                { id: "huawei", light: huaweiLogo, dark: huaweiLogoDark, label: "Huawei" },
                { id: "mikrotik", light: mikrotik, dark: mikrotik_dark, label: "MikroTik" },
                { id: "edgecore", light: edgecoreLogo, dark: edgecoreLogo, label: "Edgecore" },
                { id: "tp-link", light: tplinkLogo, dark: tplinkLogoDark, label: "TP-Link" },
                { id: "aws", light: awsLogo, dark: awsLogoDark, label: "AWS" },
                { id: "arista", light: aristaLogo, dark: aristaLogoDark, label: "Arista" },
                { id: "cisco", light: ciscoLogo, dark: ciscoLogoDark, label: "Cisco" },
                { id: "juniper", light: juniperLogo, dark: juniperLogoDark, label: "Juniper" },
                { id: "paloalto", light: paloaltoLogo, dark: paloaltoLogoDark, label: "Palo Alto" },
                { id: "fortinet", light: fortinetLogo, dark: fortinetLogoDark, label: "Fortinet" },
                { id: "sophos", light: sophosLogo, dark: sophosLogoDark, label: "Sophos" },
                { id: "microsoft", light: windowsLogo, dark: windowsLogoDark, label: "Microsoft" },
                { id: "ubuntu", light: ubuntuLogo, dark: ubuntuLogoDark, label: "Ubuntu" },
                { id: "debian", light: debianLogo, dark: debianLogoDark, label: "Debian" },
                { id: "redhat", light: redhatLogo, dark: redhatLogoDark, label: "Red Hat" },
                { id: "apple", light: appleLogo, dark: appleLogoDark, label: "Apple" },
                { id: "dell", light: dellLogo, dark: dellLogo, label: "Dell" },
                { id: "syrotech", light: syrotechLogo, dark: syrotechLogoDark, label: "Syrotech" },
              ].map((device) => (
                <div key={device.id} className="device-logo-box" title={device.label}>
                  <img 
                    src={isDarkMode ? device.dark : device.light} 
                    alt={device.label} 
                    style={
                      device.id === 'aws' 
                        ? { transform: isDarkMode ? 'scale(2.2)' : 'scale(1.3)' } 
                        : device.id === 'edgecore' 
                          ? { filter: isDarkMode ? 'brightness(0) invert(1)' : 'none', maxHeight: '30px' } 
                          : device.id === 'apple'
                            ? { transform: 'scale(2.2)', maxHeight: '35px' }
                            : undefined
                    } 
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <NewScanDrawer
        key={drawerScanType}
        open={drawerOpen}
        initialScanType={drawerScanType}
        onClose={handleDrawerClose}
        agents={[]}
        credentials={[]}
      />
    </div>
  );
};

export default NewScanV2;
